import { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronDown, X, Pipette } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ColorPickerProps {
  value?: string;
  onChange: (color: string) => void;
  label?: string;
  includeOpacity?: boolean;
}

// Removed preset palette picker UI per request

export function ColorPicker({ value = '#1f77b4', onChange, label, includeOpacity = true }: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [color, setColor] = useState(value);
  const [hexInput, setHexInput] = useState(value);
  const [opacity, setOpacity] = useState(100);
  // HSL will be initialized in useEffect, but we need to calculate initial values first
  const getInitialHslForState = () => {
    try {
      let hex = value;
      if (typeof value === 'string' && value.trim().toLowerCase().startsWith('rgba(')) {
        const m = value.match(/rgba\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([0-1]?(?:\.\d+)?)\)/i);
        if (m) {
          const r = Math.max(0, Math.min(255, parseInt(m[1])));
          const g = Math.max(0, Math.min(255, parseInt(m[2])));
          const b = Math.max(0, Math.min(255, parseInt(m[3])));
          hex = '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
        }
      }
      if (value.length === 9 && value.startsWith('#')) {
        hex = value.slice(0, 7);
      }
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (result) {
        const r = parseInt(result[1], 16) / 255;
        const g = parseInt(result[2], 16) / 255;
        const b = parseInt(result[3], 16) / 255;
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        let h = 0, s = 0, l = (max + min) / 2;
        if (max !== min) {
          const d = max - min;
          s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
          switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
            case g: h = ((b - r) / d + 2) / 6; break;
            case b: h = ((r - g) / d + 4) / 6; break;
          }
        }
        return {
          h: Math.round(h * 360),
          s: Math.round(s * 100),
          l: Math.round(l * 100)
        };
      }
    } catch {}
    return { h: 200, s: 50, l: 50 };
  };
  const initialHsl = getInitialHslForState();
  const [hue, setHue] = useState(initialHsl.h);
  const [saturation, setSaturation] = useState(initialHsl.s);
  const [lightness, setLightness] = useState(initialHsl.l);
  // Removed paletteType and presets UI
  const [inputMode, setInputMode] = useState<'hex' | 'rgb' | 'hsl'>('hex');
  const [rgbInput, setRgbInput] = useState({ r: 31, g: 119, b: 180 });
  const [hslInput, setHslInput] = useState({ h: 200, s: 50, l: 50 });
  // removed eyedropper picking state wiring
  const spectrumRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'right';
  }>({ vertical: 'bottom', horizontal: 'left' });

  // Utility functions for color conversion
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgbToHsl = (r: number, g: number, b: number) => {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  };

  const hslToRgb = (h: number, s: number, l: number) => {
    h = h / 360;
    s = s / 100;
    l = l / 100;
    let r, g, b;

    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }

    return {
      r: Math.round(r * 255),
      g: Math.round(g * 255),
      b: Math.round(b * 255)
    };
  };

  const rgbToHex = (r: number, g: number, b: number) => {
    return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
  };

  useEffect(() => {
    try {
      let hex = value;
      let opacityValue = 100;
      
      // Normalize incoming value to hex for UI, extract opacity
      if (typeof value === 'string' && value.trim().toLowerCase().startsWith('rgba(')) {
        const m = value.match(/rgba\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([0-1]?(?:\.\d+)?)\)/i);
        if (m) {
          const r = Math.max(0, Math.min(255, parseInt(m[1])));
          const g = Math.max(0, Math.min(255, parseInt(m[2])));
          const b = Math.max(0, Math.min(255, parseInt(m[3])));
          const a = Math.max(0, Math.min(1, parseFloat(m[4])));
          hex = rgbToHex(r, g, b);
          opacityValue = Math.round(a * 100);
          
          // Update RGB and HSL values
          const rgb = { r, g, b };
          const hsl = rgbToHsl(r, g, b);
          setRgbInput(rgb);
          setHslInput(hsl);
          setHue(hsl.h);
          setSaturation(hsl.s);
          setLightness(hsl.l);
          
          setColor(hex);
          setHexInput(hex);
          setOpacity(opacityValue);
          return;
        }
      }
      
      // Extract opacity if present in 8-digit hex
      if (value.length === 9 && value.startsWith('#')) {
        const opacityHex = value.slice(7, 9);
        opacityValue = Math.round((parseInt(opacityHex, 16) / 255) * 100);
        hex = value.slice(0, 7); // Use 6-digit hex for color calculations
      }
      
      // Convert hex to RGB and HSL to update picker position
      const rgb = hexToRgb(hex);
      if (rgb) {
        const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
        setRgbInput(rgb);
        setHslInput(hsl);
        setHue(hsl.h);
        setSaturation(hsl.s);
        setLightness(hsl.l);
      }
      
      setColor(hex);
      setHexInput(hex);
      setOpacity(opacityValue);
    } catch {
      const defaultHex = '#1f77b4';
      const defaultRgb = hexToRgb(defaultHex);
      if (defaultRgb) {
        const defaultHsl = rgbToHsl(defaultRgb.r, defaultRgb.g, defaultRgb.b);
        setRgbInput(defaultRgb);
        setHslInput(defaultHsl);
        setHue(defaultHsl.h);
        setSaturation(defaultHsl.s);
        setLightness(defaultHsl.l);
      }
      setColor(defaultHex);
      setHexInput(defaultHex);
      setOpacity(100);
    }
  }, [value]);

  // Close dropdown when clicking outside - but not for select dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't close if clicking on a select dropdown
      if (target.closest('[role="combobox"]') || target.closest('[role="listbox"]')) {
        return;
      }
      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Calculate dropdown position to keep it within viewport
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;
    
    const margin = 16;
    const calculatePosition = () => {
      if (!buttonRef.current) return;
      
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Estimate dropdown dimensions (w-72 = 18rem = 288px, plus padding)
      const dropdownWidth = 288;
      const dropdownHeight = 400; // Approximate height including all controls
      
      // Calculate vertical position
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const vertical = spaceBelow >= dropdownHeight + margin || spaceBelow >= spaceAbove 
        ? 'bottom' 
        : 'top';
      
      // Calculate horizontal position
      // For left alignment: check if dropdown would overflow right edge
      const wouldOverflowRight = buttonRect.left + dropdownWidth + margin > viewportWidth;
      // For right alignment: check if dropdown would overflow left edge
      const wouldOverflowLeft = buttonRect.right - dropdownWidth - margin < 0;
      
      let horizontal: 'left' | 'right' = 'left';
      if (wouldOverflowRight && !wouldOverflowLeft) {
        horizontal = 'right';
      } else if (!wouldOverflowRight) {
        horizontal = 'left';
      } else {
        // If both would overflow, choose the side with more space
        const spaceOnLeft = buttonRect.left;
        const spaceOnRight = viewportWidth - buttonRect.right;
        horizontal = spaceOnRight > spaceOnLeft ? 'left' : 'right';
      }
      
      setDropdownPosition({ vertical, horizontal });
    };
    
    // Calculate position immediately when opening
    calculatePosition();
    
    // Recalculate on window resize and scroll
    window.addEventListener('resize', calculatePosition);
    window.addEventListener('scroll', calculatePosition, true);
    
    return () => {
      window.removeEventListener('resize', calculatePosition);
      window.removeEventListener('scroll', calculatePosition, true);
    };
  }, [isOpen]);

  // Ensure the expanded color card is fully visible by nudging the nearest scroll container
  useEffect(() => {
    if (!isOpen) return;
    const el = dropdownRef.current;
    if (!el) return;
    const margin = 16;
    const getScrollParent = (node: HTMLElement | null): HTMLElement | Window => {
      let cur: HTMLElement | null = node?.parentElement || null;
      while (cur) {
        try {
          const style = window.getComputedStyle(cur);
          const canScroll = (style.overflowY === 'auto' || style.overflowY === 'scroll') && cur.scrollHeight > cur.clientHeight;
          if (canScroll) return cur;
        } catch {}
        cur = cur.parentElement;
      }
      return window;
    };
    const sp = getScrollParent(el);
    // Defer until layout settles and position is calculated
    setTimeout(() => {
      try {
        const rect = el.getBoundingClientRect();
        if (sp === window) {
          const overflowBottom = rect.bottom - window.innerHeight + margin;
          if (overflowBottom > 0) window.scrollBy({ top: overflowBottom, behavior: 'smooth' });
          const overflowTop = margin - rect.top;
          if (overflowTop > 0) window.scrollBy({ top: -overflowTop, behavior: 'smooth' });
        } else {
          const host = sp as HTMLElement;
          const hostRect = host.getBoundingClientRect();
          const overflowBottom = rect.bottom - hostRect.bottom + margin;
          if (overflowBottom > 0) host.scrollBy({ top: overflowBottom, behavior: 'smooth' });
          const overflowTop = hostRect.top + margin - rect.top;
          if (overflowTop > 0) host.scrollBy({ top: -overflowTop, behavior: 'smooth' });
        }
      } catch {
        // Fallback
        try { el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch {}
      }
    }, 10);
  }, [isOpen, dropdownPosition]);

  const handleColorChange = (newColor: string, newOpacity?: number) => {
    const finalOpacity = newOpacity !== undefined ? newOpacity : opacity;
    
    // Update opacity state if newOpacity is provided
    if (newOpacity !== undefined) {
      setOpacity(newOpacity);
    }
    
    // Prefer rgba() strings for alpha to maximize Plotly compatibility
    let finalColor: string = newColor;
    const rgb = hexToRgb(newColor);
    // Output rgba when opacity is less than 100% (includes opacity 0)
    if (includeOpacity && finalOpacity < 100 && rgb) {
      finalColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${finalOpacity / 100})`;
    }

    setColor(newColor);
    setHexInput(newColor);
    onChange(finalColor);
    
    // Update all input formats
    if (rgb) {
      setRgbInput(rgb);
      const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
      setHue(hsl.h);
      setSaturation(hsl.s);
      setLightness(hsl.l);
      setHslInput(hsl);
    }
  };

  const handleHexInputChange = (hex: string) => {
    setHexInput(hex);
    if (/^#[0-9A-F]{6}$/i.test(hex)) {
      handleColorChange(hex);
    }
  };

  const handleSpectrumClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!spectrumRef.current) return;
    const rect = spectrumRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const xPercent = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const yPercent = Math.max(0, Math.min(100, (y / rect.height) * 100));
    
    // x = saturation (0-100), y = lightness (100-0)
    const s = xPercent;
    const l = 100 - yPercent;
    
    setSaturation(s);
    setLightness(l);
    
    // Convert HSL to Hex
    const rgb = hslToRgb(hue, s, l);
    const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
    handleColorChange(hex);
  };

  const handleHueChange = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const newHue = Math.round(percent * 3.6); // 0-360
    
    setHue(newHue);
    const rgb = hslToRgb(newHue, saturation, lightness);
    const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
    handleColorChange(hex);
  };

  const handleOpacityChange = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
    
    handleColorChange(color, Math.round(percent));
  };

  const handleEyedropper = async () => {
    if ('EyeDropper' in window) {
      try {
        // @ts-ignore - EyeDropper API is not in TypeScript types yet
        const eyeDropper = new window.EyeDropper();
        const result = await eyeDropper.open();
        handleColorChange(result.sRGBHex);
      } catch (e) {
        console.log('Eye dropper cancelled');
      }
    } else {
      // Fallback for browsers that don't support EyeDropper API
      alert('Color picker is not supported in your browser');
    }
  };

  // Get the display color that includes opacity
  const getDisplayColor = () => {
    if (!includeOpacity || opacity >= 100) {
      return color;
    }
    const rgb = hexToRgb(color);
    if (rgb) {
      return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity / 100})`;
    }
    return color;
  };

  // Get the display text (show rgba if opacity < 100%)
  const getDisplayText = () => {
    if (!includeOpacity || opacity >= 100) {
      return color;
    }
    const rgb = hexToRgb(color);
    if (rgb) {
      return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${(opacity / 100).toFixed(2)})`;
    }
    return color;
  };

  return (
    <div className="space-y-1" ref={containerRef}>
      {label && (
        <Label className="text-sm text-muted-foreground">
          {label}
        </Label>
      )}
      <div className="relative">
        <Button
          ref={buttonRef}
          variant="outline"
          className="w-full justify-between h-10"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="flex items-center gap-2">
            <div className="relative w-4 h-4 rounded border border-border overflow-hidden">
              {/* Checkerboard pattern for transparency */}
              {includeOpacity && opacity < 100 && (
                <div 
                  className="absolute inset-0"
                  style={{
                    background: `
                      linear-gradient(45deg, #ddd 25%, transparent 25%),
                      linear-gradient(-45deg, #ddd 25%, transparent 25%),
                      linear-gradient(45deg, transparent 75%, #ddd 75%),
                      linear-gradient(-45deg, transparent 75%, #ddd 75%)
                    `,
                    backgroundSize: '4px 4px',
                    backgroundPosition: '0 0, 0 2px, 2px -2px, -2px 0px'
                  }}
                />
              )}
              {/* Color overlay */}
              <div 
                className="absolute inset-0"
                style={{ backgroundColor: getDisplayColor() }}
              />
            </div>
            <span className="text-sm font-mono">{getDisplayText()}</span>
          </div>
          <ChevronDown className={cn("h-4 w-4 opacity-50 transition-transform", isOpen && "rotate-180")} />
        </Button>

        {isOpen && (
          <div 
            ref={dropdownRef} 
            className={cn(
              "absolute z-50 w-72 p-3 bg-popover border rounded-md shadow-lg",
              "max-h-[calc(100vh-2rem)] overflow-y-auto",
              dropdownPosition.vertical === 'bottom' ? 'top-full mt-1' : 'bottom-full mb-1',
              dropdownPosition.horizontal === 'left' ? 'left-0' : 'right-0'
            )}
          >
            <div className="space-y-3">
              {/* Header */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Custom</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => setIsOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Color Spectrum Square - shows saturation/lightness of selected hue */}
              <div 
                ref={spectrumRef}
                className="relative h-32 rounded border border-border cursor-crosshair overflow-hidden"
                onClick={handleSpectrumClick}
              >
                {/* Horizontal gradient: white (left, sat=0%) to full hue color (right, sat=100%) */}
                <div 
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(to right, #fff, hsl(${hue}, 100%, 50%))`
                  }}
                />
                {/* Vertical overlay: transparent (top, light=100%) to black (bottom, light=0%) */}
                {/* This darkens the horizontal gradient vertically */}
                <div 
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,1))`
                  }}
                />
                {/* Position indicator */}
                <div 
                  className="absolute w-4 h-4 border-2 border-white rounded-full shadow-md pointer-events-none z-10"
                  style={{
                    left: `${saturation}%`,
                    top: `${100 - lightness}%`,
                    transform: 'translate(-50%, -50%)',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)'
                  }}
                />
              </div>

              {/* Hue Slider - Rainbow spectrum */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="h-4 w-4 text-muted-foreground cursor-pointer hover:text-foreground"
                  onClick={handleEyedropper}
                  aria-label="Pick color from screen"
                >
                  <Pipette className="h-4 w-4" />
                </button>
                <div 
                  className="flex-1 h-3 rounded cursor-pointer relative"
                  style={{
                    background: `linear-gradient(to right, 
                      hsl(0, 100%, 50%), 
                      hsl(60, 100%, 50%), 
                      hsl(120, 100%, 50%), 
                      hsl(180, 100%, 50%), 
                      hsl(240, 100%, 50%), 
                      hsl(300, 100%, 50%), 
                      hsl(360, 100%, 50%))`
                  }}
                  onClick={handleHueChange}
                >
                  {/* Hue selector indicator */}
                  <div 
                    className="absolute w-3 h-3 border-2 border-white rounded-full shadow-md pointer-events-none"
                    style={{
                      left: `${(hue / 360) * 100}%`,
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.3)'
                    }}
                  />
                </div>
              </div>

              {/* Opacity Slider */}
              {includeOpacity && (
                <div className="flex items-center gap-2">
                  <div 
                    className="w-4 h-3 rounded border border-border"
                    style={{
                      background: `
                        linear-gradient(45deg, #ddd 25%, transparent 25%),
                        linear-gradient(-45deg, #ddd 25%, transparent 25%),
                        linear-gradient(45deg, transparent 75%, #ddd 75%),
                        linear-gradient(-45deg, transparent 75%, #ddd 75%)
                      `,
                      backgroundSize: '4px 4px',
                      backgroundPosition: '0 0, 0 2px, 2px -2px, -2px 0px'
                    }}
                  />
                  <div className="flex-1 relative">
                    {/* Checkerboard background */}
                    <div 
                      className="absolute inset-0 h-3 rounded border border-border"
                      style={{
                        background: `
                          linear-gradient(45deg, #ddd 25%, transparent 25%),
                          linear-gradient(-45deg, #ddd 25%, transparent 25%),
                          linear-gradient(45deg, transparent 75%, #ddd 75%),
                          linear-gradient(-45deg, transparent 75%, #ddd 75%)
                        `,
                        backgroundSize: '4px 4px',
                        backgroundPosition: '0 0, 0 2px, 2px -2px, -2px 0px'
                      }}
                    />
                    {/* Color gradient overlay */}
                    <div 
                      className="relative h-3 rounded cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, transparent, ${color})`
                      }}
                      onClick={handleOpacityChange}
                    >
                      <div 
                        className="absolute w-3 h-3 border-2 border-white rounded-full shadow-md pointer-events-none"
                        style={{
                          left: `${opacity}%`,
                          top: '50%',
                          transform: 'translate(-50%, -50%)'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Color input - dropdown style with hex/rgb/hsl options */}
              <div className="space-y-2">
                <Select value={inputMode} onValueChange={(value: any) => setInputMode(value)}>
                  <SelectTrigger className="h-7 text-xs" onClick={(e) => e.stopPropagation()}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent 
                    onPointerDownOutside={(e) => e.preventDefault() as any}
                  >
                    <SelectItem value="hex" className="text-xs">HEX</SelectItem>
                    <SelectItem value="rgb" className="text-xs">RGB</SelectItem>
                    <SelectItem value="hsl" className="text-xs">HSL</SelectItem>
                  </SelectContent>
                </Select>

                {inputMode === 'hex' && (
                  <Input
                    value={hexInput}
                    onChange={(e) => handleHexInputChange(e.target.value)}
                    className="h-8 text-xs font-mono w-full"
                    placeholder="#000000"
                    maxLength={7}
                  />
                )}

                {inputMode === 'rgb' && (
                  <div className="grid grid-cols-3 gap-1">
                    <Input
                      type="number"
                      value={rgbInput.r}
                      onChange={(e) => {
                        const r = Math.max(0, Math.min(255, parseInt(e.target.value) || 0));
                        setRgbInput({ ...rgbInput, r });
                        const hex = rgbToHex(r, rgbInput.g, rgbInput.b);
                        handleColorChange(hex);
                      }}
                      className="h-8 text-xs text-center"
                      placeholder="R"
                      min={0}
                      max={255}
                    />
                    <Input
                      type="number"
                      value={rgbInput.g}
                      onChange={(e) => {
                        const g = Math.max(0, Math.min(255, parseInt(e.target.value) || 0));
                        setRgbInput({ ...rgbInput, g });
                        const hex = rgbToHex(rgbInput.r, g, rgbInput.b);
                        handleColorChange(hex);
                      }}
                      className="h-8 text-xs text-center"
                      placeholder="G"
                      min={0}
                      max={255}
                    />
                    <Input
                      type="number"
                      value={rgbInput.b}
                      onChange={(e) => {
                        const b = Math.max(0, Math.min(255, parseInt(e.target.value) || 0));
                        setRgbInput({ ...rgbInput, b });
                        const hex = rgbToHex(rgbInput.r, rgbInput.g, b);
                        handleColorChange(hex);
                      }}
                      className="h-8 text-xs text-center"
                      placeholder="B"
                      min={0}
                      max={255}
                    />
                  </div>
                )}

                {inputMode === 'hsl' && (
                  <div className="grid grid-cols-3 gap-1">
                    <Input
                      type="number"
                      value={hslInput.h}
                      onChange={(e) => {
                        const h = Math.max(0, Math.min(360, parseInt(e.target.value) || 0));
                        setHslInput({ ...hslInput, h });
                        const rgb = hslToRgb(h, hslInput.s, hslInput.l);
                        const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
                        handleColorChange(hex);
                      }}
                      className="h-8 text-xs text-center"
                      placeholder="H"
                      min={0}
                      max={360}
                    />
                    <Input
                      type="number"
                      value={hslInput.s}
                      onChange={(e) => {
                        const s = Math.max(0, Math.min(100, parseInt(e.target.value) || 0));
                        setHslInput({ ...hslInput, s });
                        const rgb = hslToRgb(hslInput.h, s, hslInput.l);
                        const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
                        handleColorChange(hex);
                      }}
                      className="h-8 text-xs text-center"
                      placeholder="S"
                      min={0}
                      max={100}
                    />
                    <Input
                      type="number"
                      value={hslInput.l}
                      onChange={(e) => {
                        const l = Math.max(0, Math.min(100, parseInt(e.target.value) || 0));
                        setHslInput({ ...hslInput, l });
                        const rgb = hslToRgb(hslInput.h, hslInput.s, l);
                        const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
                        handleColorChange(hex);
                      }}
                      className="h-8 text-xs text-center"
                      placeholder="L"
                      min={0}
                      max={100}
                    />
                  </div>
                )}
              </div>

              {/* Preset palette picker removed */}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}